import { useState, useEffect } from "react";
import { Landmark, Search, Sparkles, Loader2, Bot, Send } from "lucide-react";
import api from "@/lib/api";
import { FinanceDeals } from "@/components/FinanceDeals";
import { BenefitCard } from "@/components/BenefitCard";
import { toast } from "sonner";

const SPENDS = [["travel", "Travel"], ["shopping", "Shopping"], ["fuel", "Fuel"], ["dining", "Dining"], ["online", "Online"], ["general", "General"]];

const ADV_CHIPS = ["Best card for fuel spends", "Top travel rewards card", "Cashback card for online shopping", "Which card for dining?"];

export default function FinancePage() {
  const [q, setQ] = useState("");
  const [query, setQuery] = useState("");
  const [cat, setCat] = useState("travel");
  const [amt, setAmt] = useState("");
  const [sugg, setSugg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [advQ, setAdvQ] = useState("");
  const [advAns, setAdvAns] = useState("");
  const [advCards, setAdvCards] = useState(null);
  const [advBusy, setAdvBusy] = useState(false);

  useEffect(() => { const qq = new URLSearchParams(window.location.search).get("q"); if (qq) { setQ(qq); setQuery(qq); } }, []);

  const find = async () => {
    setBusy(true);
    try {
      const r = await api.post("/marketplace/public/card-finder", { spend_category: cat, monthly_spend: Number(amt) || 0 });
      setSugg(r.data?.suggestions || []);
    } catch { toast.error("Couldn't find cards right now"); setSugg([]); }
    finally { setBusy(false); }
  };

  const askAdvisor = async (question) => {
    const text = (question ?? advQ).trim();
    if (!text) return;
    setAdvQ(text); setAdvBusy(true); setAdvAns(""); setAdvCards(null);
    try {
      const r = await api.post("/marketplace/public/finance-advisor", { question: text, monthly_spend: Number(amt) || 0 });
      setAdvAns(r.data?.answer || "");
      setAdvCards(r.data?.suggestions || []);
    } catch { toast.error("Advisor is busy — try again shortly"); setAdvCards([]); }
    finally { setAdvBusy(false); }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-6" data-testid="finance-page">
      <div className="rounded-3xl bg-gradient-to-br from-indigo-600 to-violet-600 p-8 text-white">
        <p className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold"><Landmark className="h-4 w-4" /> Finance &amp; Banking</p>
        <h1 className="mt-3 text-3xl font-bold sm:text-4xl">Credit cards, loans &amp; insurance</h1>
        <p className="mt-2 max-w-xl text-sm text-white/80">Compare offers from leading banks &amp; partners, then apply online in minutes.</p>
        <div className="mt-5 flex max-w-lg gap-2">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/60" />
            <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && setQuery(q)} data-testid="finance-search" placeholder="Search credit card, loan, insurance, bank…" className="w-full rounded-full border border-white/20 bg-white/10 py-2.5 pl-10 pr-4 text-sm text-white placeholder:text-white/50 focus:outline-none" />
          </div>
          <button onClick={() => setQuery(q)} data-testid="finance-search-btn" className="rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-indigo-700">Search</button>
        </div>
      </div>

      <div className="mt-6 rounded-2xl border border-indigo-200 bg-gradient-to-br from-indigo-50 to-violet-50 p-5" data-testid="finance-advisor">
        <h2 className="flex items-center gap-2 text-lg font-bold text-slate-800"><Bot className="h-5 w-5 text-indigo-500" /> Ask the Finance Advisor</h2>
        <p className="mt-1 text-sm text-slate-500">Describe what you want in plain words — we'll match a card and link you to apply.</p>
        <div className="mt-3 flex gap-2">
          <input value={advQ} onChange={(e) => setAdvQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && askAdvisor()} data-testid="advisor-input" placeholder="e.g. best card for fuel spends" className="h-11 flex-1 rounded-full border border-slate-200 bg-white px-4 text-sm outline-none focus:border-indigo-400" />
          <button onClick={() => askAdvisor()} disabled={advBusy} data-testid="advisor-submit" className="inline-flex h-11 shrink-0 items-center gap-1.5 rounded-full brand-gradient-bg px-5 text-sm font-semibold text-white disabled:opacity-50">{advBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Ask</button>
        </div>
        <div className="mt-2 flex flex-wrap gap-2">
          {ADV_CHIPS.map((c) => <button key={c} onClick={() => askAdvisor(c)} data-testid={`advisor-chip-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`} className="rounded-full border border-indigo-200 bg-white px-3 py-1.5 text-xs font-medium text-indigo-700 transition hover:bg-indigo-100">{c}</button>)}
        </div>
        {advAns && <p className="mt-4 flex items-start gap-2 rounded-xl bg-white/80 p-3 text-sm text-slate-700 ring-1 ring-indigo-100" data-testid="advisor-answer"><Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-indigo-500" />{advAns}</p>}
        {advCards && (advCards.length ? (
          <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="advisor-results">{advCards.map((d) => <BenefitCard key={d.id} d={d} />)}</div>
        ) : (!advBusy && <p className="mt-4 text-sm text-slate-500" data-testid="advisor-empty">No matching partner cards right now — try a different question.</p>))}
      </div>

      <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5" data-testid="card-finder">
        <h2 className="flex items-center gap-2 text-lg font-bold text-slate-800"><Sparkles className="h-5 w-5 text-indigo-500" /> Card Finder</h2>
        <p className="mt-1 text-sm text-slate-500">Tell us your biggest monthly spend and we'll suggest a matching credit card.</p>
        <div className="mt-4 flex flex-wrap items-end gap-3">
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-500">Spend on</label>
            <select value={cat} onChange={(e) => setCat(e.target.value)} data-testid="finder-category" className="h-10 rounded-lg border border-slate-200 px-3 text-sm">{SPENDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-500">Monthly amount (₹)</label>
            <input value={amt} onChange={(e) => setAmt(e.target.value.replace(/[^0-9]/g, ""))} data-testid="finder-amount" placeholder="10000" className="h-10 w-40 rounded-lg border border-slate-200 px-3 text-sm" />
          </div>
          <button onClick={find} disabled={busy} data-testid="finder-submit" className="inline-flex h-10 items-center gap-1.5 rounded-full brand-gradient-bg px-5 text-sm font-semibold text-white disabled:opacity-50">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Find my card</button>
        </div>
        {sugg && (sugg.length ? (
          <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="finder-results">{sugg.map((d) => <BenefitCard key={d.id} d={d} />)}</div>
        ) : (
          <p className="mt-4 text-sm text-slate-500" data-testid="finder-empty">No matching cards right now — try a different spend category.</p>
        ))}
      </div>

      <FinanceDeals q={query} title="Top finance offers" limit={12} />
    </div>
  );
}
